import { FrameworkCategoryOption, LibraryFilters } from 'models'
import { categoryToUrl, normalizeCategorySlug } from './utils'

export type LibrarySort = 'desc' | 'asc' | 'favorites'
export type LibraryStatus = 'active' | 'inactive'

/* The filter state in the URL. Every field holds a stable API value, never a display string. */
export interface LibraryFilterState {
  categoryIds: string[]
  sources: string[]
  statuses: LibraryStatus[]
  sort: LibrarySort
  term: string
}

export const DEFAULT_LIBRARY_FILTERS: LibraryFilterState = {
  categoryIds: [],
  sources: [],
  statuses: [],
  sort: 'desc',
  term: ''
}

const PARAM = {
  categoryIds: 'category',
  sources: 'ds',
  statuses: 'status',
  sort: 'sort',
  term: 'q'
} as const

const OWNED_KEYS: string[] = Object.values(PARAM)
const SORT_VALUES: LibrarySort[] = ['desc', 'asc', 'favorites']
const STATUS_VALUES: LibraryStatus[] = ['active', 'inactive']

/* The dropdown shows a label. The URL and the API get the canonical value. */
export const SORT_LABELS: Record<LibrarySort, string> = {
  favorites: 'Favorites First',
  desc: 'Newest to Oldest',
  asc: 'Oldest to Newest'
}

export const librarySortOptionLabels = [SORT_LABELS.favorites, SORT_LABELS.desc, SORT_LABELS.asc]

export const sortFromLabel = (label: string): LibrarySort => {
  const match = SORT_VALUES.find((value) => SORT_LABELS[value] === label)

  return match ?? DEFAULT_LIBRARY_FILTERS.sort
}

/* Reads repeated keys, and splits comma-joined values for the keys that cannot contain a comma.
 * Writes always use repeated keys. */
const readList = (params: URLSearchParams, key: string, splitCommas = true): string[] => {
  const values = params
    .getAll(key)
    .flatMap((value) => (splitCommas ? value.split(',') : [value]))
    .map((value) => value.trim())
    .filter(Boolean)

  return Array.from(new Set(values))
}

export const parseLibraryFilterParams = (params: URLSearchParams): LibraryFilterState => {
  const sort = params.get(PARAM.sort) as LibrarySort | null
  const statuses = readList(params, PARAM.statuses).filter((value): value is LibraryStatus =>
    STATUS_VALUES.includes(value as LibraryStatus)
  )

  return {
    /* Category ids are an open set, so this function keeps every value. A comma is part of the
     * value, because a link can carry a display title such as "Governance, Risk & Compliance". */
    categoryIds: readList(params, PARAM.categoryIds, false),
    sources: readList(params, PARAM.sources),
    statuses,
    sort: sort && SORT_VALUES.includes(sort) ? sort : DEFAULT_LIBRARY_FILTERS.sort,
    term: (params.get(PARAM.term) ?? '').trim()
  }
}

/* Merges over `base`, so a filter change keeps the params of other pages. Sorts the lists and
 * omits the defaults, so one filter state always gives one URL. */
export const serializeLibraryFilterParams = (filters: LibraryFilterState, base?: URLSearchParams): URLSearchParams => {
  const params = new URLSearchParams(base)
  OWNED_KEYS.forEach((key) => params.delete(key))

  const appendAll = (key: string, values: string[]) => {
    const sorted = [...values].sort()
    sorted.forEach((value) => params.append(key, value))
  }

  appendAll(PARAM.categoryIds, filters.categoryIds)
  appendAll(PARAM.sources, filters.sources)
  appendAll(PARAM.statuses, filters.statuses)

  if (filters.sort !== DEFAULT_LIBRARY_FILTERS.sort) {
    params.set(PARAM.sort, filters.sort)
  }
  if (filters.term) {
    params.set(PARAM.term, filters.term)
  }

  return params
}

export const hasActiveLibraryFilters = (filters: LibraryFilterState): boolean =>
  filters.categoryIds.length > 0 || filters.sources.length > 0 || filters.statuses.length > 0

/* Serialization is canonical, so equal query strings mean equal filter state. */
export const isSameLibraryFilterState = (a: LibraryFilterState, b: LibraryFilterState): boolean =>
  serializeLibraryFilterParams(a).toString() === serializeLibraryFilterParams(b).toString()

/* A caller can link with a category id, a URL slug, or a display title. Resolve all three to the
 * id. Return an unknown value unchanged, because the filter must not disappear. */
export const resolveLibraryCategoryId = (value: string, options: FrameworkCategoryOption[]): string => {
  const raw = value.trim()
  if (!raw) return ''

  const slug = normalizeCategorySlug(raw.toLowerCase())

  const byId = options.find((option) => option.id === raw || option.id === slug)
  if (byId) return byId.id

  const byTitle = options.find((option) => option.title.toLowerCase() === raw.toLowerCase())
  if (byTitle) return byTitle.id

  /* Compare the raw value and the folded value. `threat-detection` is the Onyxia id
   * `detection-and-response`. In a custom framework it is the slug of a category with the title
   * "Threat Detection" and a different id. */
  const bySlug = options.find((option) => {
    const optionSlugs = [categoryToUrl(option.title, true), categoryToUrl(option.title.toLowerCase())]

    return optionSlugs.includes(slug) || optionSlugs.includes(raw.toLowerCase())
  })

  return bySlug?.id ?? raw
}

/* The only function that builds a link to the library, so no caller writes a query string.
 * `category` can be an id, a slug, or a display title. The page canonicalises it. */
export const buildLibraryLink = (category?: string, statuses: LibraryStatus[] = ['inactive']): string => {
  const params = serializeLibraryFilterParams({
    ...DEFAULT_LIBRARY_FILTERS,
    categoryIds: category ? [category] : [],
    statuses
  })
  const search = params.toString()

  return search ? `/library?${search}` : '/library'
}

/* Adapts the filter state to the shape that useSearchCpis takes. Category ids go in a separate
 * argument. */
export const toSearchFilters = (filters: LibraryFilterState): LibraryFilters => ({
  category: [],
  source: filters.sources,
  status: filters.statuses,
  sortBy: SORT_LABELS[filters.sort],
  frameworkCategory: ''
})
